// apps/merchant-v3/src/main.tsx
//
// Phase 6 Wave 1 — SPA entry point. React 19 createRoot + StrictMode
// + BrowserRouter (clean-path routing) + QueryClientProvider
// (TanStack Query default cache). Task 02 wires AppShell + AppRouter
// inside <App />; auth integration lands Task 03.
//
// vg-binding: PLAN:task-01
// vg-binding: PLAN:task-02
// vg-binding: P6.D-03

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { App } from './App';
import './index.css';

// D-278 — data router (createBrowserRouter/RouterProvider) instead of the
// legacy BrowserRouter. A single splat route renders the existing <App/>
// (which keeps its descendant <Routes> map) so the whole route tree is
// unchanged, but ALL navigations now flow through the data router — which is
// what unlocks `useBlocker` for the edit-wizard unsaved-changes guard.
const router = createBrowserRouter([{ path: '*', element: <App /> }]);

declare const __MERCHANT_V3_BUILD_SHA__: string;

declare global {
  interface Window {
    __BUILD_SHA__?: string;
  }
}

if (typeof window !== 'undefined') {
  window.__BUILD_SHA__ = __MERCHANT_V3_BUILD_SHA__;
}

// Stale-chunk recovery — sau mỗi deploy, các chunk lazy cũ (hash trong tên,
// vd OrderDetailPage-Xxxx.js) bị xoá khỏi server. Tab đang mở giữ index.html
// cũ sẽ tải chunk đã xoá → 404 → Vite bắn `vite:preloadError` và router render
// "Failed to fetch dynamically imported module". Ta tự reload MỘT lần để nạp
// index.html + manifest mới. sessionStorage guard chống reload-loop nếu 404
// do nguyên nhân khác (deploy hỏng thật).
if (typeof window !== 'undefined') {
  const CHUNK_RELOAD_KEY = 'pw-chunk-reloaded';
  window.addEventListener('vite:preloadError', (event) => {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    event.preventDefault();
    window.location.reload();
  });
  window.addEventListener('load', () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in merchant-v3 index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
